import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import GridItem from 'components/Grid/GridItem';
import { BodyM, HeadingLBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import Button from 'components/CustomButtons/Button';
import Add from '@material-ui/icons/Add';
import TableHeader, { IButton } from 'components/TableHeader/TableHeader';
import Table from 'components/CustomTable/CustomTable';
import { LinkCell, UserNameCell } from 'components/Cells/Cells';
import moment from 'moment';
import CanPerform from 'components/CanPerform/CanPerform';
import AddUserDialog from 'views/Users/common/AddUserDialog';
import React, { useEffect, useRef, useState } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/departmentDetailsStyle';
import { makeStyles } from '@material-ui/core';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import Edit from '@material-ui/icons/Edit';
import _ from 'lodash';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { exportToXLS } from 'utils/export/export';
import { hasPermission } from 'utils/permissions/permissions';
import { AccountsService } from 'services/accounts/accounts';
import { finalize } from 'rxjs/operators';
import AddManagerDialog from './subcomponents/AddManagerDialog';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import EditExpirationDateDialog from './subcomponents/EditExpirationDateDialog';
import UserIcon from 'components/UserIcon/UserIcon';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAllManagersProps {
	notAssigned: boolean;
	department: types.organizations.IExtendedDepartmentDetails | null;
	accounts: SingleCorpAccount[];
	deleteManagers: (managersIds: string[]) => void;
	loggedUserType: string;
	departmentId: string;
	accountId: string;
	addManagers: (managers: SingleUser[], expiration: string) => void;
	userAddedSuccessfully: () => void;
	departments: SingleDepartment[];
	expirationDateEdited: (userId: string, expirationDate: string) => void;
}

const AllManagers = ({
	notAssigned,
	department,
	accounts,
	deleteManagers,
	loggedUserType,
	departmentId,
	accountId,
	addManagers,
	userAddedSuccessfully,
	departments,
	expirationDateEdited,
}: IAllManagersProps) => {
	const [addManagerDialog, setAddManagerDialog] = useState<boolean>(false);
	const [managersSearchInputValue, setManagersSearchInputValue] = useState<string>('');
	const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
	const [filteredManagers, setFilteredManagers] = useState<types.organizations.IExtendedSingleUser[]>([]);
	const [availableManagers, setAvailableManagers] = useState<SingleUser[]>([]);
	const [filteredAvailableManagers, setFilteredAvailableManagers] = useState<SingleUser[]>([]);
	const [loadingManagers, setLoadingManagers] = useState<boolean>(false);
	const [isExpirationDateDialogOpen, setIsExpirationDateDialogOpen] = useState<boolean>(false);
	const [expirationDialogData, setExpirationDialogData] = useState<types.organizations.IExpirationDialogData>({
		userName: '',
		expirationDate: '',
		userId: '',
	});
	const classes = useStyles();
	const noAvailableManagers: boolean = filteredAvailableManagers.length === 0;
	const isMounted = useRef(true);

	const userDetailsPath = (userId: string) => {
		return `/dashboard/users/${userId}`;
	};

	const deleteManagersClicked = () => {
		const managersToDelete: string[] = _.chain(filteredManagers)
			.filter((_manager, index: number) => selectedIndexes.indexOf(index) >= 0)
			.map('userId')
			.value();
		deleteManagers(managersToDelete);
	};

	const editPermissions = (manager: types.organizations.IExtendedSingleUser) => {
		setExpirationDialogData({
			userName: manager.userName,
			expirationDate: manager.permissionExpiration,
			userId: manager.userId,
		});
		setIsExpirationDateDialogOpen(true);
	};

	const managersXlsExport = () => {
		const preparedData = (selectedIndexes.length === 0
			? filteredManagers
			: _.filter(filteredManagers, (manager, index) => selectedIndexes.indexOf(index) >= 0)
		).map((manager: SingleUser) => ({
			'User name': manager.userName,
			'Expiration date': manager.permissionExpiration || translate('unlimited'),
		}));

		exportToXLS(preparedData, `managers ${moment().format('LLL')}`);
	};

	useEffect(() => {
		if (hasPermission(loggedUserType, 'department:add-manager')) {
			setLoadingManagers(true);
			AccountsService.getManagersForAccounts(departmentId, accountId)
				.pipe(finalize(() => isMounted.current && setLoadingManagers(false)))
				.subscribe(({ managers }) => isMounted.current && setAvailableManagers(managers));
		}
	}, [departmentId, accountId, loggedUserType]);

	useEffect(() => {
		if (department && department.managers) {
			const lowercaseSearchInputValue = managersSearchInputValue.toLowerCase();
			const _filteredManagers = department.managers
				.filter((row: SingleUser) => row.userName.toLowerCase().includes(lowercaseSearchInputValue))
				.map((manager: SingleUser) => ({ ...manager, new: false }));
			setFilteredManagers(_filteredManagers);
		}
	}, [department, managersSearchInputValue]);

	useEffect(() => {
		if (department) {
			const ids = department.managers.map((manager: SingleUser) => manager.userId);
			const managers = availableManagers.filter((manager: SingleUser) => ids.indexOf(manager.userId) < 0);
			setFilteredAvailableManagers(managers);
		}
	}, [department, availableManagers]);

	useEffect(() => {
		setSelectedIndexes([]);
	}, [department]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<ConditionalRender show={!notAssigned}>
			<GridItem xs={12} className={classes.managersWrapper}>
				<HeadingLBold color="secondary-light">{translate('managers')}</HeadingLBold>
				<ConditionalRender show={!!department && department.managers.length <= 0}>
					<div className={classes.managers}>
						<div>
							<BodyM color="primary-light" disableMargin>
								{noAvailableManagers
									? translate('departments.no.manager.account')
									: translate('departments.no.manager.for.department')}
							</BodyM>
						</div>
						<Button color="primary-light" withIcon onClick={() => setAddManagerDialog(true)}>
							<Add />
							<ConditionalRender
								show={noAvailableManagers}
								onTrue={() => <>{translate('departments.add.first.manager')}</>}
								onFalse={() => <>{translate('departments.add.manager')}</>}
							/>
						</Button>
					</div>
				</ConditionalRender>
				<ConditionalRender show={!!department && department.managers.length > 0}>
					<>
						<TableHeader
							setSearchInputValue={(value) => setManagersSearchInputValue(value)}
							searchInputValue={managersSearchInputValue}
							buttons={
								{
									onClick: () => setAddManagerDialog(true),
									text: (
										<>
											<Add />
											{translate('add.manager')}
										</>
									),
									buttonProps: { withIcon: true },
								} as IButton
							}
						/>
						<Table
							total={filteredManagers.length}
							withCheckbox
							alignColumnsRight={[3]}
							onSelect={(value) => setSelectedIndexes(value)}
							selected={selectedIndexes}
							selectPanel={{
								selectedElements: selectedIndexes.length,
								deleteItems: deleteManagersClicked,
								downloadMenuItems: [
									{
										name: 'XSLX',
										onClick: managersXlsExport,
									},
								],
							}}
							withHeader
							tableProps={{
								loading: !department,
								data: filteredManagers,
								columns: [
									{
										Header: translate('table.user.name'),
										Cell: ({ original }: { original: types.organizations.IExtendedSingleUser }) =>
											original.accessible ? (
												<LinkCell
													icon={<UserIcon type="MANAGER" />}
													path={userDetailsPath(original.userId)}
													text={original.userName}
												/>
											) : (
												<UserNameCell text={original.userName} type={'MANAGER'} />
											),
										accessor: 'userName',
										minWidth: 200,
									},
									{
										Header: translate('department.details.expiration.date'),
										Cell: ({ original }: { original: types.organizations.IExtendedSingleUser }) => (
											<div className={classes.permissionsCell}>
												<BodyM color="secondary-light">
													{original.permissionExpiration
														? moment(original.permissionExpiration).format('YYYY.MM.DD')
														: translate('unlimited')}
												</BodyM>
												<CanPerform
													action="department:edit-expiration-date"
													yes={
														<div>
															<Button
																justIcon
																color="tertiary-light"
																size="sm"
																onClick={() => editPermissions(original)}
															>
																<Edit />
															</Button>
														</div>
													}
												/>
											</div>
										),
										width: 200,
										accessor: 'permissionExpiration',
									},
								],
								defaultPageSize: 10,
							}}
						/>
					</>
				</ConditionalRender>
				<ConditionalRender
					show={noAvailableManagers}
					onTrue={() => (
						<AddUserDialog
							isDialogOpen={addManagerDialog}
							setIsDialogOpen={(value) => setAddManagerDialog(value)}
							dialogHeader={translate('add.manager')}
							userType={'MANAGER'}
							accounts={accounts}
							departments={departments as any}
							userAddedSuccessfully={userAddedSuccessfully}
							toastSuccessMessage={translate('manager.user.added')}
						/>
					)}
					onFalse={() => (
						<AddManagerDialog
							isOpen={addManagerDialog}
							hideDialog={() => setAddManagerDialog(false)}
							addManagers={addManagers}
							availableManagers={filteredAvailableManagers}
							loading={loadingManagers}
						/>
					)}
				/>
				<CanPerform
					action="department:edit-expiration-date"
					yes={
						<EditExpirationDateDialog
							setIsDialogOpen={(value: boolean) => {
								setIsExpirationDateDialogOpen(value);
							}}
							isDialogOpen={isExpirationDateDialogOpen}
							data={expirationDialogData}
							departmentId={departmentId}
							expirationDateEdited={expirationDateEdited}
						/>
					}
				/>
			</GridItem>
		</ConditionalRender>
	);
};

export default AllManagers;
